/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import ServiceItemSkeleton from "../../skeleton/ServiceItemSkeleton";
import { Chips, ChipsItem, ChipsList, ChipsSection } from "../../ui/Chips";
import SearchField from "../../ui/form/SearchField";
import Skeleton from "../../skeleton/Skeleton";
import EmptyStep from "../steps/EmptyStep";
import BookingStepList from "../steps/BookingStepList";

const CategorySections = styled.div`
  display: grid;
  gap: 16px;
`;

const ChipsSkeletonRow = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledInnerWrapper = styled.div`
  display: grid;
  gap: 16px;
`;

// Search + optional category chips/sections over services (classes are services too).
const ServiceCatalog = ({
  categorized = false,
  items,
  categories,
  isLoading,
  noDataToDisplay,
  emptyMessage,
  renderItem,
}) => {
  const [searchValue, setSearchValue] = useState("");

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const filterItems = useCallback(
    (itemList) => {
      return itemList.filter((item) => item.name.toLowerCase().includes(searchValue.toLowerCase()));
    },
    [searchValue]
  );

  const renderItems = (itemList, category) => {
    return filterItems(itemList).map((item) => renderItem(item, category));
  };

  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    return categories.map((category) => ({
      ...category,
      services: filterItems(category.services),
    }));
  }, [categories, filterItems]);

  if (isLoading) {
    return (
      <StyledInnerWrapper>
        {categorized && (
          <ChipsSkeletonRow>
            {[...Array(3)].map((_, index) => (
              <Skeleton key={index} borderRadius={999} width={96} height={39} />
            ))}
          </ChipsSkeletonRow>
        )}
        <SearchField name="search" placeholder="Search" value={searchValue} onChange={handleSearchChange} />
        <BookingStepList>
          {[...Array(4)].map((_, index) => (
            <ServiceItemSkeleton key={index} />
          ))}
        </BookingStepList>
      </StyledInnerWrapper>
    );
  }

  if (!isLoading && noDataToDisplay) {
    return <EmptyStep message={emptyMessage} />;
  }

  if (!categorized) {
    return (
      <StyledInnerWrapper>
        <SearchField name="search" placeholder="Search" value={searchValue} onChange={handleSearchChange} />
        <BookingStepList>{renderItems(items)}</BookingStepList>
      </StyledInnerWrapper>
    );
  }

  return (
    <Chips defaultChipId={categories[0]?.id}>
      <StyledInnerWrapper>
        {categories.length > 1 && (
          <ChipsList>
            {categories.map(
              (category) =>
                !!category.services.length && <ChipsItem key={category.id} id={category.id} title={category.name} />
            )}
          </ChipsList>
        )}

        <SearchField name="search" placeholder="Search" value={searchValue} onChange={handleSearchChange} />
        <CategorySections>
          {filteredCategories.map(
            (category) =>
              !!category.services.length && (
                <ChipsSection key={category.id} id={category.id} title={category.name}>
                  <BookingStepList>{renderItems(category.services, category)}</BookingStepList>
                </ChipsSection>
              )
          )}
        </CategorySections>
      </StyledInnerWrapper>
    </Chips>
  );
};

export default ServiceCatalog;
